import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { CRouteList, ERouteList } from '@app/route-list.const';
import { AppConfigService } from '@core/app-config/app-config.service';
import { IConsent, IConsentSummary, IConsentTypes } from '@core/consent/consent.model';
import { IDiscounts } from '@core/discounts/models/discounts.model';
import { IframeService } from '@core/iframe/iframe.service';
import { IProduct } from '@core/product/models/product.model';
import { CStorageKeys } from '@core/storage/storage-keys.const';
import { StorageService } from '@core/storage/storage.service';
import { IVenueConfig } from '@core/venue-config/models/venue-config.model';
import { roundToTwo } from '@roller/booking-calculation-service';
import { ScriptsService } from '@roller/rdl/utilities/scripts';
import { differenceInDays } from 'date-fns';
import { DateFormatService } from '../date/date-format.service';
import { AnalyticsMappingService } from './analytics-mapping.service';
import { IProductAnalytics } from './analytics-progress/product-analytics.model';
import { IAnalyticsWindow, ICustomParams, IDefaultGtagParams, IEventParams, ITracking } from './analytics_item.model';
import { TAnalyticsAction } from './analytics.model';
import { IMetaProperties, TMetaActions } from './meta.model';
import { ITikTokParams, TTikTokEvents } from './tik-tok-analytics.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { IOrder } from '@core/order/models/order.model';
import { MatomoAnalyticsService } from './matomo-analytics.service';
import { DatadogRumService } from '@core/datadog-rum/datadog-rum.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  readonly cRoutes = CRouteList;
  readonly storageKeys = CStorageKeys;

  private window: IAnalyticsWindow;

  venueName = '';
  checkoutName = '';
  venueCurrency = 'USD';
  private venueSlug = '';
  private environment = '';
  checkoutType = 'standard-checkout';
  rollerGaMeasurementId = '';
  venueConfig: IVenueConfig | undefined;
  private defaultGtagParams: IDefaultGtagParams | undefined;
  private isLoaded = false;
  private isInIframe = false;
  private isLoggedIn = false;
  private experimentName: string | undefined;
  /** Experiment group B is shown experiments - it is not the control group. */
  private isInExperimentGroupB: boolean | undefined;

  facebook: ITracking = {
    isEnabled: false,
    isScriptAdded: false
  };

  tikTok: ITracking = {
    isEnabled: false,
    isScriptAdded: false
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone,
    private datadogRumService: DatadogRumService,
    private appConfigService: AppConfigService,
    private storageService: StorageService,
    private analyticsMapping: AnalyticsMappingService,
    private scriptsService: ScriptsService,
    private iframeService: IframeService,
    private dateFormatService: DateFormatService,
    private matomoAnalyticsService: MatomoAnalyticsService
  ) {
    this.window = this.document.defaultView as Window as IAnalyticsWindow;
    this.isInIframe = this.iframeService.getIsInsideIframe();
  }

  /**
   * For Roller's gtag measurementId we have disabled sending page views due to excessive hits.
   * Manually sending page view event on significant route changes.
   */
  onPageView(page_title: string, page_location: string, page_path: string): void {
    const action = 'page_view';

    this.internalGtag('event', action, {
      page_title,
      page_location,
      page_path,
      checkoutType: this.checkoutType
    });
    this.callMetaTag('track', 'ViewContent');
    this.matomoAnalyticsService.trackPageView(page_location, page_path);
  }

  /** Sends an event with venue data of specified action. */
  onCustomEvent(action: string, params?: ICustomParams) {
    this.internalGtag('event', action, {
      ...this.defaultGtagParams,
      ...params
    });
  }

  /**
   * Sending button id, aria.label and isLoggedIn(if needed) to GTM when the element is clicked.
   */
  onButtonClicked(id: string, label: string): void {
    const action = 'button_clicked';

    this.internalGtag('event', action, {
      id,
      label,
      checkoutType: this.checkoutType,
      ...this.defaultGtagParams
    });
    this.callMetaTag('track', 'ViewContent');
  }

  /**
   * When the user clicks on a product from home or products page (or on load).
   * @param item The parent item that contains tickets.
   * @param category Widget name (on home page) or category (if no category then blank) on Products page.
   * @param location home or products page.
   * @param index the location in the originating list.
   */
  onProductClick(item: IProduct, category: string | undefined, location: string, index: number): void {
    const action = 'view_item';
    if (!category) {
      category = 'products';
    }

    this.internalGtag('event', action, {
      ...this.defaultGtagParams,
      currency: this.venueCurrency,
      items: this.analyticsMapping.mapProductToAnalyticsItem(item, category, location, index),
      checkoutType: this.checkoutType
    });

    this.callTikTok('ViewContent', this.analyticsMapping.mapProductToTikTokItem(item, location, this.venueCurrency));
    this.matomoAnalyticsService.trackEcommerceView(item, category);
  }

  /** Widget internal call to action. Note for call to action the index can only be 1 */
  onCallToActionSelect(item: IProduct | undefined, event_category: string, event_label: string): void {
    const action = 'view_item';

    this.internalGtag('event', action, {
      ...this.defaultGtagParams,
      non_interaction: false,
      items: this.analyticsMapping.mapProductToAnalyticsItem(item, event_category, event_label, 1),
      event_category,
      event_label,
      checkoutType: this.checkoutType
    });
  }

  /**
   * Analytics cart events - add to cart and remove from cart.
   * value here is the value that has been changed (including fees) - not the current total value of the cart.
   * @param action TAnalyticsAction, typically Add, Remove, View
   * @param items - An array or IOrderItems being added/removed from cart
   * @param value - The difference between total order value on cart event and items being added
   * @param totalOrderValue - The total order value on cart event
   * @param page - The current page of cart event e.g Product
   * @param location - The current url of the cart event e.g /Products
   */
  onCartEvents(
    action: TAnalyticsAction,
    items: IOrderItem[],
    value: number,
    totalOrderValue: number,
    page: string,
    location: string
  ): void {
    this.internalGtag('event', action, {
      ...this.defaultGtagParams,
      currency: this.venueCurrency,
      value: value,
      items: this.analyticsMapping.mapOrderItemsToAnalyticsItem(items, this.venueCurrency, page, location, true),
      checkoutType: this.checkoutType,
      avgDaysAhead: this.averageDaysAheadForBooking(items),
      action_location: location
    });
    if (action === 'add_to_cart') {
      this.callMetaTag('track', 'AddToCart', {
        contents: this.analyticsMapping.mapOrderItemsToMetaContentItems(items),
        value: value,
        content_ids: items.map((item: IOrderItem) => item.productId),
        content_name: page,
        content_type: location,
        currency: this.venueCurrency
      });
      this.callTikTok(
        'AddToCart',
        this.analyticsMapping.mapOrderItemsToTikTokContents(items, this.venueCurrency, page)
      );
    }

    this.matomoAnalyticsService.trackCartEvent(items, totalOrderValue);
  }

  /** Fires on details page when navigates to payment page. */
  onNavigateToPaymentPage(): void {
    this.callTikTok('AddPaymentInfo');
  }

  /** Fires on pages with lists - home, products and addons. */
  onViewList(item_list_id: string, item_list_name: string, itemList: IProductAnalytics[]): void {
    this.internalGtag('event', 'view_item_list', {
      ...this.defaultGtagParams,
      item_list_id,
      item_list_name,
      items: this.analyticsMapping.mapProductListToGA4AnalyticsViewList(itemList, this.venueCurrency),
      checkoutType: this.checkoutType
    });
  }

  /** When a significant route loads the checkout step is sent to analytics along with the current checkout basket and value. */
  checkoutProgress(
    items: IOrderItem[],
    value: number,
    page: string,
    url: string,
    isInit: boolean,
    current_step?: ERouteList
  ): void {
    let property: ICustomParams = {
      ...this.defaultGtagParams,
      currency: this.venueCurrency,
      value,
      items: this.analyticsMapping.mapOrderItemsToAnalyticsItem(items, this.venueCurrency, page, url),
      checkoutType: this.checkoutType
    };
    if (current_step) {
      property = { ...property, current_step };
    }

    this.internalGtag('event', isInit ? 'begin_checkout' : 'checkout_progress', property);

    if (isInit) {
      this.callMetaTag('track', 'InitiateCheckout');
      this.callTikTok('InitiateCheckout');
    }
  }

  /** On init of the confirmation page sends through the booking details and items in cart. */
  onPurchase(order: IOrder): void {
    const action = 'purchase';
    const property: ICustomParams = {
      ...this.defaultGtagParams,
      currency: this.venueCurrency,
      value: order.amounts.total,
      tax: order.amounts.tax,
      fees: order.amounts.fees,
      transaction_id: order.draftBooking?.uniqueBookingId,
      affiliation: this.checkoutName,
      items: this.analyticsMapping.mapOrderItemsToAnalyticsItem(order.items, this.venueCurrency),
      checkoutType: this.checkoutType
    };
    if (order.discounts.length) {
      property.coupon = order.discounts.map((disc: IDiscounts) => disc.code).join(', ');
    }

    this.internalGtag('event', action, property);
    this.callMetaTag('track', 'Purchase', {
      value: order.amounts.total,
      currency: this.venueCurrency,
      contents: this.analyticsMapping.mapOrderItemsToMetaContentItems(order.items)
    });
    this.callTikTok('CompletePayment', {
      ...this.analyticsMapping.mapOrderItemsToTikTokContents(order.items, this.venueCurrency),
      value: order.amounts.total
    });

    this.matomoAnalyticsService.trackEcommerceOrder(order);
  }

  /**
   * Fire a a manual window dataLayer event
   * @param event can be any event specified in GTM
   */
  onDataLayerEvent(event: string): void {
    if (!this.isLoaded) {
      return;
    }

    return this.zone.runOutsideAngular(() => {
      try {
        this.window.dataLayer.push({ event });
      } catch (e) {
        console.warn('Failed to push GTM dataLayer event', e);
      }
    });
  }

  /** Update the logged in Status and the default gtag params - these get sent with most events */
  setLoggedInStatus(isLoggedIn: boolean): void {
    this.isLoggedIn = isLoggedIn;
    this.defaultGtagParams = this.createDefaultGtagParams();
  }

  /**
   * Sets the local values for experiment name and group, which in turn are used in the default
   * GtagParams when the gtm scripts are added
   */
  setExperimentalStatus(experiment: string, isInExperimentGroupB: boolean): void {
    this.experimentName = experiment;
    this.isInExperimentGroupB = isInExperimentGroupB;
  }

  /** Gtag params - get sent with most events.  */
  private createDefaultGtagParams() {
    const defaultTags: IDefaultGtagParams = {
      environment: this.environment,
      venue: this.venueName,
      venue_slug: this.venueSlug,
      checkout_name: this.checkoutName,
      isInIframe: this.isInIframe,
      isLoggedIn: this.isLoggedIn
    };
    if (this.experimentName) {
      defaultTags.experimentName = this.experimentName;
      defaultTags.isInExperimentGroupB = this.isInExperimentGroupB;
    }

    return defaultTags;
  }

  /** Quick private method to find the average days ahead a booking is made, 0 is today, 1 tomorrow etc. Rounds to two */
  private averageDaysAheadForBooking(items: IOrderItem[]): number {
    const start = this.dateFormatService.getDateWithoutTime(new Date());
    const listDaysAhead = items.map((item) => differenceInDays(item.bookingDate, start));
    const findAverage = listDaysAhead.reduce((acc: number, days: number) => acc + days, 0) / listDaysAhead.length;
    return roundToTwo(findAverage);
  }

  /**
   * Create a promise that runs out side angular to reference the window.gtag and send the required data.
   * event_callback is triggered by a successful call to window.gtag to allow the resolution of the promise.
   * @see: https://developers.google.com/analytics/devguides/collection/gtagjs/events
   */
  private internalGtag(
    cmd: 'set' | 'config' | 'event' | 'consent',
    action: string,
    properties: IEventParams = {}
  ): Promise<void> | void {
    // may miss the first page view event
    if (!this.isLoaded) {
      return;
    }

    return this.zone.runOutsideAngular(
      () =>
        new Promise<void>((resolve) => {
          try {
            const tmr = setTimeout(() => {
              console.warn(`gtag call timed out: ${cmd} - ${action}`);
              resolve();
            }, 5000);

            // Performs the event call resolving with the event callback
            this.window?.gtag(cmd, action, {
              ...properties,
              event_callback: () => {
                clearTimeout(tmr);
                resolve();
              }
            });
          } catch (_) {
            resolve();
          }
        })
    );
  }

  /** Facebook pixel is now meta pixel, runs try catch on the window object. */
  private callMetaTag(cmd: 'track', action: TMetaActions, properties?: IMetaProperties): Promise<void> | void {
    if (!this.isLoaded || !this.facebook.isEnabled || !this.facebook.isScriptAdded) {
      return;
    }

    return this.zone.runOutsideAngular(() => {
      try {
        this.window.fbq(cmd, action, {
          ...properties
        });
      } catch (e) {
        console.warn('Meta pixel send error', e);
        this.facebook.isEnabled = false;
      }
    });
  }

  /** Access to the window object, runs try catch on the ttq.track. */
  private callTikTok(event: TTikTokEvents, properties?: ITikTokParams): Promise<void> | void {
    if (!this.isLoaded || !this.tikTok.isEnabled || !this.tikTok.isScriptAdded) {
      return;
    }

    return this.zone.runOutsideAngular(() => {
      try {
        this.window.ttq.track(event, {
          ...properties
        });
      } catch (e) {
        console.warn('TikTok send error', e);
        this.tikTok.isEnabled = false;
      }
    });
  }

  /**
   * On getting the venue's configuration data venue component will add the appropriate Google scripts with the
   * supplied keys as well as set some common values to be used throughout.
   */
  addAnalyticsScripts(
    rollerGtmId = 'GTM-5PRJ8Z7',
    rollerGtagId = 'G-TJBSBCGW4J',
    config: IVenueConfig,
    checkoutSlug: string
  ): void {
    this.venueConfig = config;

    if (config.analytics.googleTrackingId) {
      this.scriptsService.addScriptElement(undefined, this.createGtagScript(config.analytics.googleTrackingId));
    } else {
      this.scriptsService.addScriptElement(undefined, this.createGtagScript());
    }

    this.scriptsService.addScriptElement(undefined, this.createGoogleTagManagerScript(rollerGtmId));

    if (config.analytics.googleTagManagerEnabled && !!config.analytics.googleTagManagerData) {
      this.scriptsService.addScriptElement(
        undefined,
        this.createGoogleTagManagerScript(config.analytics.googleTagManagerData)
      );
    }

    this.venueSlug = this.storageService.getItem<string>(this.storageKeys.apiKey, false) as string;
    this.venueCurrency = config.venueSettings.currencyCode;
    this.venueName = config.venueSettings.name;
    this.checkoutName = checkoutSlug;

    this.rollerGaMeasurementId = rollerGtagId;
    if (config.checkoutType === 'foodAndBeverageCheckout') {
      this.checkoutType = 'food-and-beverage-checkout';
    }

    this.environment = this.appConfigService.getEnvValue<string>('env') as string;
    this.defaultGtagParams = this.createDefaultGtagParams();

    this.isLoaded = true;
  }

  /** Updates the consent settings to enable use of analytics sending  */
  updateConsent(consentItem: IConsent): Promise<void> | void {
    if (!this.venueConfig || !this.isLoaded) {
      return;
    }

    /** Marketing */
    if (consentItem.consent.isMarketingGranted) {
      if (this.venueConfig.analytics.facebookTrackingEnabled && !!this.venueConfig.analytics.facebookTrackingId) {
        if (!this.facebook.isScriptAdded) {
          this.scriptsService.addScriptElement(
            undefined,
            this.createFacebook(this.venueConfig.analytics.facebookTrackingId)
          );
          this.facebook.isScriptAdded = true;
        }

        this.facebook.isEnabled = true;
      }

      if (this.venueConfig.analytics.tikTokTrackingEnabled && !!this.venueConfig.analytics.tikTokTrackingId) {
        if (!this.tikTok.isScriptAdded) {
          this.scriptsService.addScriptElement(
            undefined,
            this.createTikTok(this.venueConfig.analytics.tikTokTrackingId)
          );
          this.tikTok.isScriptAdded = true;
        }

        this.tikTok.isEnabled = true;
      }
    } else {
      this.facebook.isEnabled = false;
      this.tikTok.isEnabled = false;
    }

    /** Performance */
    if (consentItem.consent.isPerformanceGranted) {
      this.matomoAnalyticsService.setup(this.venueConfig, this.checkoutName);
      this.datadogRumService.init(this.appConfigService.getEnvConfig());
    }

    const properties = this.mapConsentSummaryToGoogleConsentTypes(consentItem.consent);
    // note that the consent update does not have any event_callback
    return this.zone.runOutsideAngular(
      () =>
        new Promise<void>((resolve) => {
          try {
            this.window.gtag('consent', 'update', properties);
          } catch (e) {
            console.warn('gtag consent update error', e);
            resolve();
          }
        })
    );
  }

  /** Maps the consent summary (Roller model) through to the google Consent model. */
  private mapConsentSummaryToGoogleConsentTypes(summary: IConsentSummary): IConsentTypes {
    return {
      ad_storage: summary.isMarketingGranted ? 'granted' : 'denied',
      analytics_storage: summary.isPerformanceGranted ? 'granted' : 'denied',
      functionality_storage: 'granted',
      personalization_storage: summary.isMarketingGranted ? 'granted' : 'denied',
      security_storage: 'granted'
    };
  }

  /**
   * Creates the string for the gtag and data layer to be added to the header.
   * Part of creation is the default setting to deny consent for ad_storage and analytics_storage
   */
  private createGtagScript(gTag?: string, properties?: ICustomParams): string {
    let gtagScript = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('consent', 'default', {
      'ad_storage': 'denied',
      'analytics_storage': 'denied',
      'personalization_storage': 'denied'
    });
    gtag('js', new Date());`;

    if (!gTag) {
      return gtagScript;
    }

    if (this.isInIframe) {
      properties = {
        ...properties,
        cookie_flags: 'secure;samesite=none'
      };
    }

    if (properties) {
      return (gtagScript += `gtag('config', '${gTag}', ${JSON.stringify(properties)});`);
    }

    return (gtagScript += `gtag('config', '${gTag}');`);
  }

  /** Creates the string for the google tag manager to be added to the header. */
  private createGoogleTagManagerScript(gtmId: string): string {
    const src = 'https://www.googletagmanager.com/gtm.js?id=';

    return `
  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  '${src}'+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${gtmId}');`;
  }

  private createFacebook(metaId: string): string {
    return `
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', '${metaId}');
    `;
  }

  private createTikTok(tikTokTrackingId: string): string {
    return `!function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group",
      "enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
      for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],
      n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},
      ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},
      ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",
      o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};

      ttq.load('${tikTokTrackingId}');
      ttq.page();
    }(window, document, 'ttq');`;
  }
}
